import styled, { css } from 'styled-components/macro';
import { Icon360Dark, PlayArrow } from '@yme/assets/icons';
import { Button } from '@yme/atoms/button';
import { MiniButton } from '@yme/atoms/mini-button';
import { SVGIconStyled } from '@yme/atoms/svg-icon';
import { VideoPlayerStyled } from '@yme/atoms/video-player';
import { colorBlackTransparent100, colorBlackTransparent300, colorBlackTransparent500, colorBlackTransparent900, colorCoreBlack900Base, colorCoreGray300, colorCoreGray500, colorCoreWhite1000, colorWhiteTransparent000000, colorWhiteTransparent500, defaultTransparent, } from '@yme/tokens/colors';
import { elevationBottomLvl2 } from '@yme/tokens/elevations';
import { ADAPTIVE } from '@yme/tokens/screen';
import { INDENT } from '@yme/tokens/sizes';
import { Z_INDEX } from '@yme/tokens/zindex';
import { AspectRatio } from '@yme/utils/constants';
import { getTextStyles } from '@yme/utils/styles';
import { Counter } from './Counter';
import { VIDEO, VIEW360 } from './constants';
const THUMBNAILS_ICON_MAP = {
    [VIEW360]: Icon360Dark,
    [VIDEO]: PlayArrow,
};
const getCoordinate = (currentIndex, isRightToLeft) => isRightToLeft ? currentIndex * 100 : -currentIndex * 100;
const getBorderColor = (isActive) => {
    if (isActive) {
        return colorBlackTransparent900;
    }
    return colorCoreGray500;
};
export const GalleryContainerStyled = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${({ isInLightbox }) => isInLightbox && 'height: 100%'};
`;
export const GalleryItemContainerStyled = styled.div `
  position: relative;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  background-color: ${colorCoreWhite1000};

  aspect-ratio: ${({ isSquareRatio }) => isSquareRatio ? AspectRatio.Square : AspectRatio.RectangleLevel3};

  ${({ hasPadding }) => hasPadding && `padding: ${INDENT.sm} ${INDENT.sm} 0`};

  ${ADAPTIVE.maxWidth.mobile} {
    ${({ hasPadding }) => hasPadding && `padding: ${INDENT.xs} ${INDENT.xs} 0`};
  }

  ${ADAPTIVE.maxWidth.tablet} {
    height: auto;

    ${({ isInLightbox }) => isInLightbox &&
    css `
        max-width: 100%;
        width: 100%;
        margin-top: auto;

        @media screen and (orientation: landscape) {
          @media only screen and (max-height: 768px) {
            padding-bottom: 0;
            width: auto;
            height: 74vh;
          }

          @media only screen and (max-height: 685px) {
            height: 58vh;
          }
        }
      `};
  }

  @media only screen and (max-height: 425px) {
    margin-top: 0;
  }
`;
export const GalleryTrackStyled = styled.div `
  position: relative;
  width: 100%;
  border: 1px solid ${colorCoreGray500};
  box-sizing: border-box;
  overflow: hidden;
  height: 100%;

  ${({ isInLightbox }) => isInLightbox && 'outline: none;'}

  ${({ isSquareRatio }) => isSquareRatio && `aspect-ratio: ${AspectRatio.Square}`};

  img {
    object-fit: ${({ isInLightbox }) => (isInLightbox ? 'cover' : 'contain')};
  }
`;
export const GalleryListStyled = styled.div `
  position: absolute;
  top: 0;
  left: ${({ currentIndex, isRightToLeft }) => currentIndex && getCoordinate(currentIndex, isRightToLeft)}%;
  display: flex;
  width: 100%;
  height: 100%;
  transition: left 150ms linear;

  cursor: ${({ isDraggable }) => (isDraggable ? 'grabbing' : 'grab')};
`;
export const GalleryItemStyled = styled.div `
  display: flex;
  position: relative;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  flex-shrink: 0;
  flex-grow: 1;

  img {
    display: block;
    width: 100%;
    height: 100%;
    transform: ${({ isHovered }) => (isHovered ? 'scale(2)' : 'none')};
  }

  ${ADAPTIVE.maxWidth.tablet} {
    height: auto;
  }

  ${VideoPlayerStyled} {
    ${({ isInLightbox }) => isInLightbox && 'height: 100%;'};

    width: 100%;
    aspect-ratio: ${AspectRatio.RectangleLevel3};
  }
`;
export const ControlButtonStyled = styled(Button) `
  z-index: ${Z_INDEX.level2};
  position: absolute;
  ${({ isPrev, hasContainerPadding }) => {
    const indent = hasContainerPadding ? INDENT.l : INDENT.xs;
    return isPrev ? `left: ${indent}` : `right: ${indent}`;
}};

  ${({ isView360 }) => isView360 && 'display:none;'}

  top: 50%;
  background-color: ${colorBlackTransparent300};
  transform: translateY(-50%);

  ${({ isVideo }) => !isVideo &&
    css `
      ${ADAPTIVE.maxWidth.tablet} {
        display: none;
      }
    `}

  &:hover {
    background-color: ${colorBlackTransparent500};

    svg path {
      fill: transparent;
    }
  }

  svg path {
    stroke: ${colorCoreWhite1000};
    fill: transparent;
  }
`;
export const ThumbnailsContainerStyled = styled.div `
  position: relative;
  margin-top: ${INDENT.sm};
  ${({ hasPadding }) => hasPadding && `margin: ${INDENT.s} ${INDENT.sm} ${INDENT.sm} ${INDENT.sm}`};

  ${ADAPTIVE.maxWidth.tablet} {
    margin-top: ${INDENT.xs};
    margin: ${({ isInLightbox }) => (isInLightbox ? INDENT.sm : INDENT.xs)} auto auto;
  }

  ${({ withDots, isInLightbox }) => withDots &&
    isInLightbox &&
    css `
      @media screen and (orientation: landscape) {
        margin-top: 0;
      }
    `};
`;
export const DotsContainerStyled = styled.div `
  ${({ isInLightbox }) => isInLightbox &&
    css `
      @media screen and (orientation: landscape) {
        display: none;
      }
    `}
`;
export const ThumbnailsTrackStyled = styled.div `
  border-right: 1px solid ${defaultTransparent};
  border-left: 1px solid ${defaultTransparent};
  overflow-y: hidden;
  overflow-x: scroll;
  cursor: ${({ isGrabbing }) => (isGrabbing ? 'grabbing' : 'grab')};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;
export const ThumbnailsStyled = styled.div `
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;
  height: 100%;
  transition: transform 150ms linear;
  user-select: none;

  ${({ isOnScreen, amount, indent, isRightToLeft }) => !isOnScreen &&
    css `
      &::after {
        content: '';
        position: absolute;
        top: 0;
        ${isRightToLeft ? 'left: 0' : 'right: 0'};
        display: block;
        width: ${amount &&
        `calc(100% / ${amount} - ${indent} + ${parseInt(indent, 10) / amount}px)`};
        height: 100%;
        pointer-events: none;

        background-image: linear-gradient(
          ${isRightToLeft ? 'to right' : 'to left'},
          ${colorCoreWhite1000},
          ${colorWhiteTransparent000000}
        );
      }
    `}
`;
export const ThumbnailItemWrapperStyled = styled.div `
  width: ${({ amount, indent }) => amount && `calc(100% / ${amount} - ${indent} + ${parseInt(indent, 10) / amount}px)`};
  flex-shrink: 0;
  box-sizing: border-box;
  margin: ${({ indent }) => indent && `0 calc(${indent} / 2)`};
  padding-bottom: 1px;
  padding-right: 1px;

  &:first-child {
    ${({ isRightToLeft }) => (isRightToLeft ? 'margin-right: 0' : 'margin-left: 0')};
  }

  &:last-child {
    ${({ isRightToLeft }) => (isRightToLeft ? 'margin-left: 0' : 'margin-right: 0')};
  }
`;
export const ThumbnailItemStyled = styled.div `
  position: relative;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid ${({ isActive }) => getBorderColor(isActive)};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    display: block;
    width: 100%;
    height: 100%;
    background-color: ${({ isActive }) => !isActive && colorWhiteTransparent500};
    transition: background-color 0.3s;
    z-index: ${Z_INDEX.level1};
  }

  ${({ type }) => type &&
    css `
      &::after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        display: block;
        width: 100%;
        height: 100%;
        background-image: url(${THUMBNAILS_ICON_MAP[type]});
        background-position: 50% 50%;
        background-repeat: no-repeat;
      }
    `};

  &:hover::before {
    background-color: transparent;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    aspect-ratio: ${({ aspectRatio }) => aspectRatio};
    object-fit: cover;
  }
`;
export const InfoContainerStyled = styled.div `
  display: flex;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  min-height: 52px;
  padding: ${INDENT.sm} ${INDENT.sm} 0 ${INDENT.sm};
  box-sizing: border-box;

  ${ADAPTIVE.maxWidth.tablet} {
    padding-bottom: ${INDENT.sm};
  }

  ${ADAPTIVE.maxWidth.mobile} {
    padding: ${INDENT.xs};
  }

  ${({ isMobile }) => isMobile &&
    css `
      @media screen and (orientation: landscape) {
        @media only screen and (max-height: 768px) {
          padding-bottom: 0;
        }

        @media only screen and (max-height: 685px) {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
        }
      }
    `};
`;
export const GalleryCounter = styled(Counter) `
  position: absolute;
  top: ${INDENT.xs};
  z-index: ${Z_INDEX.level1};

  ${({ isRightToLeft }) => (isRightToLeft ? `left: ${INDENT.xs};` : `right: ${INDENT.xs};`)}
`;
export const LightboxCounter = styled(Counter) `
  ${({ isRightToLeft }) => (isRightToLeft ? 'margin-right: auto' : 'margin-left: auto')};
`;
export const InfoContainerFooterStyled = styled(InfoContainerStyled) `
  align-items: flex-start;
  padding-top: ${INDENT.s};

  ${ADAPTIVE.maxWidth.tablet} {
    order: 3;
  }
`;
export const DisclaimerStyled = styled.p `
  display: flex;
  align-items: flex-start;
  margin: 0;
  padding-top: 6px;
  color: ${colorCoreBlack900Base};

  ${getTextStyles('Ubuntu', 400, 14, 20)}

  ${SVGIconStyled} {
    ${({ isRightToLeft }) => isRightToLeft ? `margin-left: ${INDENT.xxxs}` : `margin-right: ${INDENT.xxxs}`};
  }
`;
export const DownloadButtonStyled = styled(MiniButton) `
  flex-shrink: 0;
  ${({ isRTL }) => (isRTL ? 'margin-right: auto' : 'margin-left: auto')};

  &:hover {
    background-color: ${colorBlackTransparent100};
  }
`;
export const DownloadButtonMobileStyled = styled(Button) `
  border: 1px solid ${colorCoreGray300};
  box-shadow: ${elevationBottomLvl2};
`;
